import type { TargetingProps } from '@openx/types/targeting/targeting';
import { type TypeParams, type VideoData, type VideoFormState, VideoOption } from '@openx/types/targeting/video';
import { noop } from '@openx/utils/noop';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeferredFieldUpdate } from '../../utils/hooks/useDeferredFieldUpdate';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage';
import { useGetFilteredFields } from '../shared/hooks/useGetFilteredFields';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { MaxAdDurationItem } from './components/MaxAdDuration/MaxAdDurationItem';
import { PlacementAndFormat } from './components/PlacementAndFormat';
import { RewardedVideoItem } from './components/RewardedVideo/RewardedVideoItem';
import { VideoSkipabilityItem } from './components/VideoSkipability/VideoSkipabilityItem';
import { placementAndFormat } from './constants/constants';
import { useIsApplyDisabled } from './hooks/useIsApplyDisabled';
import { Items } from './Items';
import { TopBox } from './TopBox';
import { isEmptyState, mapVideoToApi, mapVideoToFormState } from './utils/utils';
import { VideoReadOnly } from './VideoReadOnly';

export const Video = memo(function Video({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
}: TargetingProps<VideoData>) {
  const { t } = useTranslation();
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);
  const initState = useMemo(() => mapVideoToFormState(cloneDeep(targetingParams)), [targetingParams]);
  const [params, updateParams] = useState<VideoFormState>(initState);
  const [type, onTypeChange] = useState<VideoOption>(VideoOption.IN_STREAM_VIDEO_POSITION);
  const isDataUpdated = useMemo(() => !isEqual(targetingParams, mapVideoToApi(params)), [params, targetingParams]);
  const isApplyDisabled = useIsApplyDisabled(params);

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const scheduleDeferredUpdate = useDeferredFieldUpdate(field, onFieldUpdateExtended, mapVideoToApi);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdateExtended(field, mapVideoToApi(params)),
    restoreParams: () => updateParams({ ...initState }),
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    updateParams(mapVideoToFormState(null));
  }, [onFieldUpdateExtended, field]);

  const onRemoveParameter = useCallback(
    (option: VideoOption) => {
      updateParams(prev => {
        const defaults = mapVideoToFormState(null);
        const next: VideoFormState = {
          ...prev,
          [option]: defaults[option],
        };

        // Defer the field update to avoid setState-inside-setState
        scheduleDeferredUpdate(next);

        return next;
      });
    },
    [scheduleDeferredUpdate]
  );

  const onChange = useCallback(
    (typeParam: TypeParams) => {
      setUserChangedInitialValue(true);
      updateParams(params => ({ ...params, [type]: typeParam }));
    },
    [type]
  );

  const isValidFormat = (type: string): type is VideoOption => {
    return Object.values(VideoOption).includes(type as VideoOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<VideoOption>(isValidFormat, onDrawerOpen, onTypeChange);

  const targetingFields = omit(params, [
    VideoOption.MAX_AD_DURATION,
    VideoOption.REWARDED_VIDEO,
    VideoOption.VIDEO_SKIPABILITY,
  ]);

  const payloadVideoTypes = useGetFilteredFields(targetingFields || {});

  const hasBothPlcmntFormat = placementAndFormat.every(type => payloadVideoTypes.includes(type));

  const videoTypes = useMemo(
    () =>
      hasBothPlcmntFormat
        ? payloadVideoTypes.filter(type => type !== VideoOption.PLACEMENT && type !== VideoOption.PRESENTATION_FORMAT)
        : payloadVideoTypes,
    [hasBothPlcmntFormat, payloadVideoTypes]
  );

  const isStateEmpty = isEmptyState(params);
  const rewardedVideo = params?.[VideoOption.REWARDED_VIDEO];
  const maxAdDuration = params?.[VideoOption.MAX_AD_DURATION];
  const videoSkipability = params?.[VideoOption.VIDEO_SKIPABILITY];

  return (
    <>
      <VideoReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        targetingParams={initState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={onRemoveAll}
        readonly={readonly}
        onRemoveParameter={onRemoveParameter}
      />
      {isOpen && (
        <TargetingDrawer
          name={name}
          onClose={onDrawerClose}
          onApply={onDrawerApply}
          isApplyDisabled={!isDataUpdated || isApplyDisabled}
        >
          <TopBox params={params} onChange={onChange} selectedType={type} onTypeChange={onTypeChange} />

          {isStateEmpty && <EmptyFilterMessage text={t('No filters applied.')} />}

          {!isStateEmpty &&
            videoTypes.map((type, index) => {
              const shouldAddDimensionChipLine =
                payloadVideoTypes.length - 1 > index ||
                !!rewardedVideo ||
                !!videoSkipability ||
                !!maxAdDuration ||
                hasBothPlcmntFormat;

              return (
                !!params[type].val.size && (
                  <div key={type}>
                    <Items params={params[type]} type={type} readonly={!!readonly} onChange={updateParams} />

                    {shouldAddDimensionChipLine && <DimensionChipLine />}
                  </div>
                )
              );
            })}

          {hasBothPlcmntFormat && (
            <PlacementAndFormat
              placementAndFormat={placementAndFormat}
              params={params}
              updateParams={updateParams}
              readonly={!!readonly}
              shouldAddDimensionLine={!!rewardedVideo}
            />
          )}

          {!isStateEmpty && !!maxAdDuration && (
            <MaxAdDurationItem
              params={maxAdDuration}
              readonly={!!readonly}
              shouldAddDimensionLine={!!rewardedVideo}
              onChange={updateParams}
            />
          )}

          {!isStateEmpty && !!rewardedVideo && (
            <RewardedVideoItem
              params={rewardedVideo}
              readonly={!!readonly}
              shouldAddDimensionLine={!!videoSkipability}
              onChange={updateParams}
            />
          )}

          {!isStateEmpty && !!videoSkipability && (
            <VideoSkipabilityItem params={videoSkipability} readonly={!!readonly} onChange={updateParams} />
          )}
        </TargetingDrawer>
      )}
    </>
  );
});
